import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../helper/apiCall';
import ENDPOINTS from '../../constants/endpoints';

// Async thunk for fetching unpaid students
export const fetchUnpaidStudents = createAsyncThunk(
    'reports/fetchUnpaidStudents',
    async (arg, thunkAPI) => {
        try {
            // Geriye dönük uyum: eskiden düz branchId geçiliyordu.
            const { branchId, placeId } = typeof arg === 'object' && arg !== null ? arg : { branchId: arg };
            const query = placeId ? `?branchId=${branchId}&placeId=${placeId}` : `?branchId=${branchId}`;
            const response = await client.get(`${ENDPOINTS.REPORTS.UNPAID_STUDENTS}${query}`, {
                token: thunkAPI.getState().auth.token
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Async thunk for fetching attendance report list
export const fetchAttendanceReportList = createAsyncThunk(
    'reports/fetchAttendanceReportList',
    async (params, thunkAPI) => {
        try {
            const response = await client.post(ENDPOINTS.REPORTS.STUDENT_ATTENDANCE_LIST, params, {
                token: thunkAPI.getState().auth.token
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Async thunk for fetching attendance report summary
export const fetchAttendanceReportSummary = createAsyncThunk(
    'reports/fetchAttendanceReportSummary',
    async (params, thunkAPI) => {
        try {
            const response = await client.post(ENDPOINTS.REPORTS.STUDENT_ATTENDANCE_SUMMARY, params, {
                token: thunkAPI.getState().auth.token
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Async thunk for fetching student package report summary
export const fetchStudentPackageReportSummary = createAsyncThunk(
    'reports/fetchStudentPackageReportSummary',
    async (params, thunkAPI) => {
        try {
            const response = await client.post(ENDPOINTS.REPORTS.STUDENT_PACKAGE_SUMMARY, params, {
                token: thunkAPI.getState().auth.token
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Async thunk for fetching student package report list
export const fetchStudentPackageReportList = createAsyncThunk(
    'reports/fetchStudentPackageReportList',
    async (params, thunkAPI) => {
        try {
            const response = await client.post(ENDPOINTS.REPORTS.STUDENT_PACKAGE_LIST, params, {
                token: thunkAPI.getState().auth.token
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Async thunk for fetching unpaid students report (detailed)
export const fetchUnpaidStudentsReport = createAsyncThunk(
    'reports/fetchUnpaidStudentsReport',
    async (params, thunkAPI) => {
        try {
            const response = await client.post(ENDPOINTS.REPORTS.UNPAID_STUDENTS, params, {
                token: thunkAPI.getState().auth.token
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getBirthdayStudents = createAsyncThunk(
  'reports/getBirthdayStudents',
  async (arg, thunkAPI) => {
    try {
      // Geriye dönük uyum: eskiden düz branchId geçiliyordu.
      const { branchId, placeId } = typeof arg === 'object' && arg !== null ? arg : { branchId: arg };
      const query = placeId ? `?branchId=${branchId}&placeId=${placeId}` : `?branchId=${branchId}`;
      const response = await client.get(
        `${ENDPOINTS.REPORTS.BIRTHDAY_STUDENTS}${query}`,
        { token: thunkAPI.getState().auth.token }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || error);
    }
  }
);

const initialState = {
    unpaidStudents: [],
    attendanceReport: {
        list: [],
        summary: null,
        loading: false,
        error: null
    },
    studentPackageReport: {
        list: [],
        summary: null,
        loading: false,
        error: null
    },
    unpaidStudentsReport: {
        data: [],
        loading: false,
        error: null
    },
    loading: false,
    error: null,
    birthdayStudents: [],
    birthdayStudentsLoading: false,
    birthdayStudentsError: null,
};

const reportsSlice = createSlice({
    name: 'reports',
    initialState,
    reducers: {
        clearUnpaidStudents: (state) => {
            state.unpaidStudents = [];
            state.error = null;
        },
        clearAttendanceReport: (state) => {
            state.attendanceReport = {
                list: [],
                summary: null,
                loading: false,
                error: null
            };
        },
        clearStudentPackageReport: (state) => {
            state.studentPackageReport = {
                list: [],
                summary: null,
                loading: false,
                error: null
            };
        },
        clearUnpaidStudentsReport: (state) => {
            state.unpaidStudentsReport = {
                data: [],
                loading: false,
                error: null
            };
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch unpaid students (legacy)
            .addCase(fetchUnpaidStudents.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUnpaidStudents.fulfilled, (state, action) => {
                state.loading = false;
                state.unpaidStudents = action.payload;
                state.error = null;
            })
            .addCase(fetchUnpaidStudents.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Bir hata oluştu';
            })
            
            // Fetch attendance report list
            .addCase(fetchAttendanceReportList.pending, (state) => {
                state.attendanceReport.loading = true;
                state.attendanceReport.error = null;
            })
            .addCase(fetchAttendanceReportList.fulfilled, (state, action) => {
                state.attendanceReport.loading = false;
                state.attendanceReport.list = action.payload;
                state.attendanceReport.error = null;
            })
            .addCase(fetchAttendanceReportList.rejected, (state, action) => {
                state.attendanceReport.loading = false;
                state.attendanceReport.error = action.payload || 'Yoklama raporu yüklenirken bir hata oluştu';
            })
            
            // Fetch attendance report summary
            .addCase(fetchAttendanceReportSummary.pending, (state) => {
                state.attendanceReport.loading = true;
                state.attendanceReport.error = null;
            })
            .addCase(fetchAttendanceReportSummary.fulfilled, (state, action) => {
                state.attendanceReport.loading = false;
                state.attendanceReport.summary = action.payload;
                state.attendanceReport.error = null;
            })
            .addCase(fetchAttendanceReportSummary.rejected, (state, action) => {
                state.attendanceReport.loading = false;
                state.attendanceReport.error = action.payload || 'Yoklama özeti yüklenirken bir hata oluştu';
            })
            
            // Fetch student package report list
            .addCase(fetchStudentPackageReportList.pending, (state) => {
                state.studentPackageReport.loading = true;
                state.studentPackageReport.error = null;
            })
            .addCase(fetchStudentPackageReportList.fulfilled, (state, action) => {
                state.studentPackageReport.loading = false;
                state.studentPackageReport.list = action.payload;
                state.studentPackageReport.error = null;
            })
            .addCase(fetchStudentPackageReportList.rejected, (state, action) => {
                state.studentPackageReport.loading = false;
                state.studentPackageReport.error = action.payload || 'Aidat raporu yüklenirken bir hata oluştu';
            })
            
            // Fetch student package report summary
            .addCase(fetchStudentPackageReportSummary.pending, (state) => {
                state.studentPackageReport.loading = true;
                state.studentPackageReport.error = null;
            })
            .addCase(fetchStudentPackageReportSummary.fulfilled, (state, action) => {
                state.studentPackageReport.loading = false;
                state.studentPackageReport.summary = action.payload;
                state.studentPackageReport.error = null;
            })
            .addCase(fetchStudentPackageReportSummary.rejected, (state, action) => {
                state.studentPackageReport.loading = false;
                state.studentPackageReport.error = action.payload || 'Aidat özeti yüklenirken bir hata oluştu';
            })
            
            // Fetch unpaid students report (detailed)
            .addCase(fetchUnpaidStudentsReport.pending, (state) => {
                state.unpaidStudentsReport.loading = true;
                state.unpaidStudentsReport.error = null;
            })
            .addCase(fetchUnpaidStudentsReport.fulfilled, (state, action) => {
                state.unpaidStudentsReport.loading = false;
                state.unpaidStudentsReport.data = action.payload;
                state.unpaidStudentsReport.error = null;
            })
            .addCase(fetchUnpaidStudentsReport.rejected, (state, action) => {
                state.unpaidStudentsReport.loading = false;
                state.unpaidStudentsReport.error = action.payload || 'Borçlu öğrenciler raporu yüklenirken bir hata oluştu';
            })
            .addCase(getBirthdayStudents.pending, (state) => {
                state.birthdayStudentsLoading = true;
                state.birthdayStudentsError = null;
            })
            .addCase(getBirthdayStudents.fulfilled, (state, action) => {
                state.birthdayStudentsLoading = false;
                state.birthdayStudents = action.payload;
            })
            .addCase(getBirthdayStudents.rejected, (state, action) => {
                state.birthdayStudentsLoading = false;
                state.birthdayStudentsError = action.payload;
            });
    },
});

export const { 
    clearUnpaidStudents, 
    clearAttendanceReport, 
    clearStudentPackageReport,
    clearUnpaidStudentsReport,
    clearError 
} = reportsSlice.actions;
export default reportsSlice.reducer; 